import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Background from 'components/Layout/Background';
import { translatePortfolioCode } from 'helpers/helpers';
import Info from 'components/Tooltip/Info';
import FreeSwitch from 'components/FormFields/FreeSwitch';
import ControlButtons from 'components/ControlButtons';

import { RootStore } from 'stores/rootStore';
import { SignatureStore } from 'stores/account/signatureStore';
import useStyles, { usePortfolioStyles } from 'components/Bucket/styles'; //, { useDevelopmentStyles }
import PortfolioTableSimple from 'components/PortfolioTables/PortfolioTableSimple';
import { PortfolioType } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';

export interface Params {
  accountId: string;
}

interface Props {
  rootStore: RootStore;
  store: SignatureStore;
  hedgingAllowed: boolean;
}

const Settings: React.FC<Props> = ({ rootStore, store, hedgingAllowed }) => {
  const { t } = useTranslation();
  const [hedging, setHedging] = useState(false);

  const classes = useStyles({ simple: true });
  const portfolioClasses = usePortfolioStyles();
  const currentBucket = store.buckets ? store.buckets[store.activeBucket] : undefined;

  useEffect(() => {
    setHedging(!!currentBucket?.currencyHedging);
  }, [currentBucket]);

  return store.settings && store.flow && currentBucket ? (
    <>
      <Background>
        <Typography variant="h1">{t(store.flow.steps[store.flowStepIndex].title)}</Typography>

        <Typography className={portfolioClasses.mainText}>{t('documents_portfolio_setting')}</Typography>

        <Grid container item xs={12} spacing={2} className={portfolioClasses.mainText}>
          <Grid item xs={6}>
            {t('bucket_selectedInvestmentStrategy')}
            <Info
              content={t('bucket_selectedInvestmentStrategy_tooltip')}
              iconProps={{ fontSize: 'small' }}
              className={classes.tooltip}
            />
          </Grid>
          <Grid item xs={6} style={{ fontWeight: 'bold' }}>
            {currentBucket.portfolio.code.type === PortfolioType.IND
              ? currentBucket.portfolio.code.title
              : translatePortfolioCode(currentBucket.portfolio.code.code)}
          </Grid>
          {hedgingAllowed && (
            <>
              <Grid item xs={6}>
                <span>{t('accountDetail_plan_currencyHedging')}</span>
                <Info
                  content={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('accountDetail_plan_currencyHedging_client_tooltip', {
                          interpolation: { escapeValue: false },
                        }),
                      }}
                    />
                  }
                  iconProps={{ fontSize: 'small' }}
                  className={classes.tooltip}
                />
              </Grid>
              <Grid item xs={6}>
                <FreeSwitch
                  id={`${currentBucket.type}_hedging`}
                  checked={currentBucket.currencyHedging}
                  afterChange={async () => {
                    await store.confirmSettings(currentBucket.type, !hedging, false);
                    setHedging(!hedging);
                  }}
                  //disabled={confirmed}
                />
              </Grid>
            </>
          )}

          <Grid item xs={6}>
            {t('accountDetail_plan_automaticRebalancing')}
            <Info
              content={
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(`tooltip_plan_rebalancing`, {
                      interpolation: { escapeValue: false },
                    }),
                  }}
                />
              }
              iconProps={{ fontSize: 'small' }}
              className={classes.tooltip}
            />
          </Grid>
          <Grid item xs={6} style={{ fontWeight: 'bold' }}>
            {t(currentBucket.automaticRebalancing ? 'general_on' : 'general_off')}
          </Grid>

          <Grid item xs={12} style={{ marginTop: '8px' }}>
            <PortfolioTableSimple
              data={currentBucket.portfolio?.assetCategories}
              savingIsins={store.settings.options.specialAssets.savings}
            />
          </Grid>
        </Grid>
      </Background>
      <ControlButtons
        isSubmitting={false}
        nextText={t('general_confirmAndNext')}
        onBack={() => store.goBack()}
        hideBack={store.flowStepIndex === 0}
        onNext={() => store.confirmSettings(currentBucket.type, hedging, true)}
      />
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Settings);
